import { Fragment, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileText, Users, Download, Printer, Send, BellRing, Building2, MessageCircle, MapPin, Search, Check, ChevronsUpDown, Mail, History } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { inr, fmtDate } from "@/lib/format";
import { printOutstanding, printPaymentReminder, printBulkReminders, printStatement, printTdsDeductionSummary, printReminderHistory, printNoteRegister, printNoteSummary, printNoteApprovalLog, printNoteAuditPack } from "@/lib/print";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

import { BRANCHES as OFFICES } from "@/lib/branches";

const waLink = (phone, text) => {
  const digits = String(phone || "").replace(/\D/g, "");
  const full = digits.length === 10 ? "91" + digits : digits;
  return `https://wa.me/${full}?text=${encodeURIComponent(text)}`;
};

const RESPONSE_OPTS = ["Promised to Pay", "Disputed", "No Reply"];

function ReminderHistoryDialog({ partyFilter, customers }) {
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState("");
  useEffect(() => {
    if (open) api.get("/reminder-log/all").then((r) => setLogs(r.data || [])).catch(() => setLogs([]));
  }, [open]);
  const shown = (logs || []).filter((l) => partyFilter === "All" || String(l.customer_name || "").trim().toLowerCase() === partyFilter.trim().toLowerCase());
  const d = (l) => drafts[l.id] || { response: l.response || "", promised_date: l.promised_date || "", response_note: l.response_note || "" };
  const saveResp = async (l) => {
    const v = d(l);
    if (!v.response) { toast.info("Pick a response first"); return; }
    setSaving(l.id);
    try {
      await api.put(`/reminder-log/${l.id}/response`, v);
      setLogs((ls) => ls.map((x) => (x.id === l.id ? { ...x, ...v } : x)));
      toast.success(`Response saved for ${l.customer_name}`);
      if (v.response === "Disputed") {
        try {
          const cust = (customers || []).find((x) => (x.name || "").trim().toLowerCase() === String(l.customer_name || "").trim().toLowerCase());
          const branch = cust?.branch || "";
          const st = (await api.get("/staff-master")).data || [];
          const pool = st.filter((s) => (s.status || "Active") !== "Inactive" && (s.branch || "Chennai") === (branch || "Chennai") && s.phone);
          const mgr = pool.find((s) => String(s.designation || "").toLowerCase().includes("manager")) || pool[0];
          const msg = `*NTC — Bill Dispute Raised* ⚠\n\nCustomer: ${l.customer_name}${branch ? ` (${branch} branch)` : ""}\nBill(s): ${(l.bills || []).join(", ") || "—"}\nOutstanding: ${inr(l.total_balance)}\nDispute note: ${v.response_note || "—"}\n\nPlease contact the customer and resolve this dispute, then update it in the Outstanding Report → Disputed list.\n\n— Nisha Transport Corporation`;
          const ph = String(mgr?.phone || "").replace(/\D/g, "");
          window.open(ph ? `https://wa.me/91${ph.slice(-10)}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
          toast.success(`Dispute alert opened on WhatsApp for ${mgr?.name || "the"}${branch ? ` (${branch})` : ""} manager`);
        } catch { /* alert is best-effort */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving("");
    }
  };
  const fmtTs = (s) => (s ? `${fmtDate(s)} ${new Date(s).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : "—");
  return (
    <>
      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setOpen(true)} data-testid="reminder-history-btn">
        <History className="h-4 w-4" /> Reminder History
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[760px]" data-testid="reminder-history-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between pr-6">
              <DialogTitle className="font-display">Reminder History{partyFilter !== "All" ? ` — ${partyFilter}` : ""}</DialogTitle>
              <Button variant="outline" size="sm" onClick={() => printReminderHistory(logs || [], partyFilter !== "All" ? partyFilter : null)} disabled={!shown.length} data-testid="reminder-history-print-btn">
                <Printer className="mr-1.5 h-4 w-4" /> Print
              </Button>
            </div>
          </DialogHeader>
          <div className="max-h-[420px] overflow-y-auto rounded-md border">
            {logs === null && <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>}
            {logs && shown.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">No reminders sent yet.</div>}
            {shown.map((l) => (
              <div key={l.id} className="border-b px-3 py-2 text-sm last:border-0" data-testid="reminder-history-row">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <span className="font-medium">{l.customer_name}</span>
                    <span className="ml-2 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-500">{l.channel || "whatsapp"}</span>
                    {l.bills?.length ? <span className="ml-2 text-xs text-muted-foreground">Bill(s) {l.bills.join(", ")}</span> : null}
                  </div>
                  <div className="whitespace-nowrap text-xs text-muted-foreground">{fmtTs(l.sent_at)} · {inr(l.total_balance)}</div>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <Select value={d(l).response} onValueChange={(v) => setDrafts((s) => ({ ...s, [l.id]: { ...d(l), response: v } }))}>
                    <SelectTrigger className="h-7 w-[150px] text-xs" data-testid="reminder-response-select"><SelectValue placeholder="Customer response…" /></SelectTrigger>
                    <SelectContent>{RESPONSE_OPTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                  {d(l).response === "Promised to Pay" && (
                    <Input type="date" className="h-7 w-[140px] text-xs" value={d(l).promised_date} onChange={(e) => setDrafts((s) => ({ ...s, [l.id]: { ...d(l), promised_date: e.target.value } }))} data-testid="reminder-promised-date" />
                  )}
                  <Input className="h-7 flex-1 text-xs" placeholder="Note (e.g. will pay after GST refund)" value={d(l).response_note} onChange={(e) => setDrafts((s) => ({ ...s, [l.id]: { ...d(l), response_note: e.target.value } }))} data-testid="reminder-response-note" />
                  <Button size="sm" className="h-7 text-xs" onClick={() => saveResp(l)} disabled={saving === l.id} data-testid="reminder-response-save">
                    {saving === l.id ? "Saving…" : "Save"}
                  </Button>
                  {l.response && !drafts[l.id] && <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold text-white ${l.response === "Promised to Pay" ? "bg-emerald-600" : l.response === "Disputed" ? "bg-red-600" : "bg-slate-500"}`} data-testid="reminder-response-badge">{l.response}{l.promised_date ? ` · by ${fmtDate(l.promised_date)}` : ""}</span>}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DisputedBillsDialog() {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [saving, setSaving] = useState("");
  const load = () => api.get("/reminders/disputed").then((r) => setList(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!list.length) return null;
  const resolve = async (l) => {
    setSaving(l.id);
    try {
      await api.put(`/reminder-log/${l.id}/response`, { response: "Resolved", promised_date: "", response_note: l.response_note || "" });
      toast.success(`${l.customer_name} dispute marked resolved`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving("");
    }
  };
  return (
    <>
      <Button variant="outline" size="sm" className="gap-1.5 border-red-300 text-red-700 hover:bg-red-50" onClick={() => setOpen(true)} data-testid="disputed-bills-btn">
        <BellRing className="h-4 w-4" /> Disputed ({list.length})
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[640px]" data-testid="disputed-bills-dialog">
          <DialogHeader><DialogTitle className="font-display">Disputed Bills — {list.length} open dispute(s)</DialogTitle></DialogHeader>
          <div className="max-h-[400px] overflow-y-auto rounded-md border">
            {list.map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-3 border-b px-3 py-2 text-sm last:border-0" data-testid="disputed-bill-row">
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{l.customer_name} <span className="ml-1 text-xs text-muted-foreground">Bill(s) {(l.bills || []).join(", ") || "—"} · {inr(l.total_balance)}</span></div>
                  <div className="text-xs text-red-700">{l.response_note || "No note"} {l.response_at ? `· disputed ${fmtDate(l.response_at)}` : ""}</div>
                </div>
                <Button size="sm" variant="outline" className="h-7 shrink-0 border-emerald-300 text-xs text-emerald-700 hover:bg-emerald-50" onClick={() => resolve(l)} disabled={saving === l.id} data-testid="disputed-resolve-btn">
                  {saving === l.id ? "Saving…" : "Mark Resolved"}
                </Button>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Mark a dispute resolved once the branch settles it with the customer — resolved bills leave this list.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AutoWeeklyDialog({ onStampChange }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [running, setRunning] = useState(false);
  const load = () => api.get("/reminders/auto-settings").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false, days: 30, last_run: null }));
  useEffect(() => { if (open) load(); }, [open]);
  const save = async (next) => {
    try {
      const res = await api.put("/reminders/auto-settings", { enabled: next.enabled, days: next.days, stamp_days: next.stamp_days });
      setCfg((c) => ({ ...c, ...res.data }));
      onStampChange && onStampChange(res.data.stamp_days || 60);
      toast.success(res.data.enabled ? `Auto reminders ON — every Monday 9am for bills ${res.data.days}+ days overdue` : "Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/reminders/auto-run");
      toast.success(`Run complete — ${r.data.sent.length} emailed, ${r.data.skipped_no_email.length} skipped (no email)${r.data.errors.length ? `, ${r.data.errors.length} failed` : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Run failed");
    } finally {
      setRunning(false);
    }
  };
  return (
    <>
      <Button variant="outline" size="sm" className={`gap-1.5 ${cfg?.enabled ? "border-emerald-300 text-emerald-700" : ""}`} onClick={() => setOpen(true)} data-testid="auto-weekly-btn">
        <BellRing className="h-4 w-4" /> Auto Weekly
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[480px]" data-testid="auto-weekly-dialog">
          <DialogHeader><DialogTitle className="font-display">Auto Weekly Reminders</DialogTitle></DialogHeader>
          {!cfg ? (
            <div className="py-4 text-center text-sm text-muted-foreground">Loading…</div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-md border px-3 py-2.5">
                <div>
                  <div className="text-sm font-semibold">Send every Monday 9:00 AM</div>
                  <div className="text-xs text-muted-foreground">Emails a reminder letter to every customer with overdue bills</div>
                </div>
                <Switch checked={cfg.enabled} onCheckedChange={(v) => save({ ...cfg, enabled: v })} data-testid="auto-weekly-toggle" />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Remind when overdue ≥</Label>
                <Input type="number" className="h-8 w-[80px]" value={cfg.days} onChange={(e) => setCfg((c) => ({ ...c, days: Number(e.target.value) || 1 }))} onBlur={() => save(cfg)} data-testid="auto-weekly-days" />
                <span className="text-sm text-muted-foreground">days</span>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Red OVERDUE stamp when ≥</Label>
                <Input type="number" className="h-8 w-[80px]" value={cfg.stamp_days ?? 60} onChange={(e) => setCfg((c) => ({ ...c, stamp_days: Number(e.target.value) || 1 }))} onBlur={() => save(cfg)} data-testid="auto-weekly-stamp-days" />
                <span className="text-sm text-muted-foreground">days (on printed reminder letters)</span>
              </div>
              {cfg.last_run && (
                <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="auto-weekly-last-run">
                  <b>Last run:</b> {fmtDate(cfg.last_run.at)} — {cfg.last_run.sent?.length || 0} emailed, {cfg.last_run.skipped_no_email?.length || 0} skipped (no email){cfg.last_run.errors?.length ? `, ${cfg.last_run.errors.length} failed` : ""}
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Customers without a saved email are skipped.</span>
                <Button size="sm" variant="secondary" onClick={runNow} disabled={running} data-testid="auto-weekly-run-now">
                  {running ? "Running…" : "Run Now"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function PendingNotesDialog({ onDecided }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState("");
  const [rejectId, setRejectId] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const load = () => api.get("/notes/pending").then((r) => setList(r.data.notes || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!list.length) return null;
  const decide = async (n, action, reason = "") => {
    setBusy(n.id + action);
    try {
      await api.post("/notes/decide", { kind: n.kind, id: n.id, action, reason });
      toast.success(`${n.kind === "credit" ? "Credit" : "Debit"} Note ${inr(n.amount)} ${action === "approve" ? "approved — now hits the ledger" : "rejected"}`);
      setRejectId("");
      setRejectReason("");
      load();
      onDecided && onDecided();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy("");
    }
  };
  return (
    <>
      <Button variant="outline" size="sm" className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => setOpen(true)} data-testid="pending-notes-btn">
        <BellRing className="h-4 w-4" /> Approve Notes ({list.length})
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[680px]" data-testid="pending-notes-dialog">
          <DialogHeader><DialogTitle className="font-display">Notes Awaiting Approval</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Branch-entered credit/debit notes do not hit the ledger until you approve them here.</p>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {list.map((n) => (
              <div key={n.id} className="flex items-center gap-3 rounded-md border border-border px-3 py-2" data-testid="pending-note-row">
                <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${n.kind === "credit" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                  {n.kind === "credit" ? "CN" : "DN"}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">
                    {n.party} — <span className={n.kind === "credit" ? "text-amber-700" : "text-red-600"}>{inr(n.amount)}</span>
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {n.kind === "credit" ? "Challan" : "Bill"} {String(n.ref || "—").replace(/^(INV|VHC)[-\s]?/i, "")} · {n.date ? fmtDate(n.date) : "—"}
                    {n.branch ? ` · ${n.branch}` : ""}{n.reason ? ` · ${n.reason}` : ""}{n.entered_by ? ` · by ${n.entered_by}` : ""}
                  </div>
                </div>
                <div className="ml-auto flex shrink-0 gap-1.5">
                  {rejectId === n.id ? (
                    <>
                      <Input
                        className="h-7 w-[180px] text-xs"
                        placeholder="Why rejected? (branch sees this)"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        autoFocus
                        data-testid="reject-reason-input"
                      />
                      <Button size="sm" className="h-7 bg-red-600 px-2.5 text-xs text-white hover:bg-red-700" disabled={!!busy} onClick={() => decide(n, "reject", rejectReason)} data-testid="confirm-reject-btn">
                        {busy === n.id + "reject" ? "…" : "Confirm"}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => { setRejectId(""); setRejectReason(""); }} data-testid="cancel-reject-btn">
                        ✕
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" className="h-7 bg-emerald-600 px-2.5 text-xs text-white hover:bg-emerald-700" disabled={!!busy} onClick={() => decide(n, "approve")} data-testid="approve-note-btn">
                        {busy === n.id + "approve" ? "…" : "Approve"}
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 border-red-300 px-2.5 text-xs text-red-600 hover:bg-red-50" disabled={!!busy} onClick={() => { setRejectId(n.id); setRejectReason(""); }} data-testid="reject-note-btn">
                        Reject
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function NoteEmailDialog() {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (open && !cfg) api.get("/notes/email-settings").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false, ca_email: "", last_sent_month: "" }));
  }, [open, cfg]);
  const save = async (next) => {
    try {
      const res = await api.put("/notes/email-settings", { enabled: next.enabled, ca_email: next.ca_email });
      setCfg((c) => ({ ...c, ...res.data }));
      toast.success(res.data.enabled ? "Monthly note register email ON — sent to the CA on the 1st morning" : "Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  const sendNow = async () => {
    setSending(true);
    try {
      await api.put("/notes/email-settings", { enabled: cfg.enabled, ca_email: cfg.ca_email });
      const r = await api.post("/notes/email-register", { month, ca_email: cfg.ca_email });
      toast.success(`Note register for ${month} emailed to ${r.data.to} (${r.data.cn} CN / ${r.data.dn} DN)`);
      setCfg((c) => ({ ...c, last_sent_month: r.data.month }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };
  return (
    <>
      <Button variant="outline" size="sm" className={`gap-1.5 ${cfg?.enabled ? "border-emerald-300 text-emerald-700" : ""}`} onClick={() => setOpen(true)} data-testid="note-email-btn">
        <Mail className="h-4 w-4" /> Note Email
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[480px]" data-testid="note-email-dialog">
          <DialogHeader><DialogTitle className="font-display">CA Note Register Email</DialogTitle></DialogHeader>
          {!cfg ? (
            <div className="py-4 text-center text-sm text-muted-foreground">Loading…</div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-md border px-3 py-2.5">
                <div>
                  <div className="text-sm font-semibold">Mail the CA every month end</div>
                  <div className="text-xs text-muted-foreground">Sends last month's credit/debit note register on the 1st morning (~9am)</div>
                </div>
                <Switch checked={cfg.enabled} onCheckedChange={(v) => save({ ...cfg, enabled: v })} data-testid="note-email-toggle" />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-sm">CA's email address</Label>
                <Input type="email" placeholder="ca@example.com" value={cfg.ca_email} onChange={(e) => setCfg((c) => ({ ...c, ca_email: e.target.value }))} onBlur={() => save(cfg)} data-testid="note-email-ca-email" />
              </div>
              {cfg.last_sent_month && (
                <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="note-email-last-sent">
                  <b>Last register sent:</b> {cfg.last_sent_month}
                </div>
              )}
              <div className="flex items-end justify-between gap-3">
                <div className="grid gap-1.5">
                  <Label className="text-sm">Send a month now</Label>
                  <Input type="month" className="h-8 w-[160px]" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="note-email-month" />
                </div>
                <Button size="sm" variant="secondary" onClick={sendNow} disabled={sending || !cfg.ca_email || !month} data-testid="note-email-send-now">
                  {sending ? "Sending…" : "Send Now"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Until the email domain is verified on Resend, mails deliver only to the account owner's inbox.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Outstanding() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const extra = isAdmin ? 3 : 0;
  const ADMIN_COLS = ["TDS", "Deduction", "Debit Note"];
  const adminVals = (r) => [Number(r.tds) || 0, Number(r.deduction) || 0, Number(r.debit_note) || 0];
  const ADMIN_KEYS = ["tds", "deduction", "debit_note"];
  const adminSum = (list, k) => list.reduce((s, r) => s + (Number(r[k]) || 0), 0);
  const [data, setData] = useState(null);
  const [view, setView] = useState("bill");
  const [officeFilter, setOfficeFilter] = useState("All");
  const [partyFilter, setPartyFilter] = useState("All");
  const [custSearch, setCustSearch] = useState("");
  const [codeSearch, setCodeSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [overdueDays, setOverdueDays] = useState(30);
  const [customers, setCustomers] = useState([]);
  const [profile, setProfile] = useState(null);
  const [stampDays, setStampDays] = useState(60);
  const [partyOpen, setPartyOpen] = useState(false);
  const [remLogs, setRemLogs] = useState({});
  const loadRemLogs = () =>
    api.get("/reminder-log").then((r) => {
      const m = {};
      (r.data?.rows || []).forEach((lg) => { m[lg.customer_name.trim().toLowerCase()] = lg; });
      setRemLogs(m);
    }).catch(() => {});
  const logReminder = async (customer, bills, total) => {
    try {
      await api.post("/reminder-log", { customer_name: customer, bills, total_balance: total });
      loadRemLogs();
    } catch (e) { /* non-blocking */ }
  };
  const daysSinceReminded = (name) => {
    const lg = remLogs[(name || "").trim().toLowerCase()];
    if (!lg?.sent_at) return null;
    return Math.floor((Date.now() - new Date(lg.sent_at).getTime()) / 86400000);
  };
  const [payScore, setPayScore] = useState(null);
  useEffect(() => {
    if (partyFilter === "All") return setPayScore(null);
    api.get("/reports/payment-score", { params: { customer_name: partyFilter } }).then((r) => setPayScore(r.data)).catch(() => setPayScore(null));
  }, [partyFilter]);

  useEffect(() => {
    api
      .get("/reports/outstanding")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/company-profile").then((r) => setProfile(r.data)).catch(() => {});
    api.get("/reminders/auto-settings").then((r) => setStampDays(r.data.stamp_days || 60)).catch(() => {});
    loadRemLogs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadStatement = async (customerName, rows) => {
    let cid = rows?.find((r) => r.customer_id)?.customer_id;
    if (!cid) {
      const c = customers.find((x) => (x.name || "").trim().toLowerCase() === String(customerName || "").trim().toLowerCase());
      cid = c?.id;
    }
    if (!cid) {
      toast.error(`No customer record found for "${customerName}" — add them in Customer Master to generate a statement.`);
      return;
    }
    try {
      const r = await api.get(`/reports/statement/${cid}`);
      printStatement(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const stripInv = (v) => String(v || "").replace(/^INV[-\s]?/i, "") || "—";
  const noDays = (r) => {
    if (r.days != null && !isNaN(Number(r.days))) return Number(r.days);
    const d = r.bill_date || r.lr_date;
    if (!d) return "—";
    const dt = new Date(d);
    if (isNaN(dt)) return "—";
    const n = Math.floor((Date.now() - dt.getTime()) / 86400000);
    return n >= 0 ? n : "—";
  };
  const rowKey = (r) =>
    [r.invoice_no, r.lr_no, r.from_place, r.destination, r.total, r.lr_date, r.customer_name]
      .map((x) => (x ?? "").toString())
      .join("|");
  const toggleRow = (r) =>
    setSelected((prev) => {
      const n = new Set(prev);
      const k = rowKey(r);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  const isSelected = (r) => selected.has(rowKey(r));
  const codeFor = (r) =>
    r.customer_code ||
    customers.find((x) => (r.customer_id && x.id === r.customer_id) || (x.name || "").trim().toLowerCase() === String(r.customer_name || "").trim().toLowerCase())?.customer_code ||
    "";
  const addrFor = (r) => {
    if (r.customer_address) return r.customer_address;
    const c = customers.find((x) => (r.customer_id && x.id === r.customer_id) || (x.name || "").trim().toLowerCase() === String(r.customer_name || "").trim().toLowerCase());
    if (!c) return "";
    if (String(c.billing_customer_address || "").trim()) return c.billing_customer_address.trim();
    const part = String(c.address || "").split(",").map((s) => s.trim()).filter(Boolean).pop() || c.state || "";
    const pin = String(c.pincode || "").trim();
    return pin && part && !part.includes(pin) ? `${part}-${pin}` : part || pin;
  };

  const exportCsv = () => {
    if (!data) return;
    const rows = visibleRows;
    let csv;
    const tdsCols = "";
    const tdsVals = () => [];
    const bucketVals = (r) => {
      const dd = Number(noDays(r));
      const bal = Number(r.net_balance) || 0;
      const b = isNaN(dd) ? 0 : dd > 60 ? 2 : dd > 30 ? 1 : 0;
      return [b === 0 ? bal : "", b === 1 ? bal : "", b === 2 ? bal : ""];
    };
    const bucketSums = rows.reduce((acc, r) => {
      const dd = Number(noDays(r));
      acc[isNaN(dd) ? 0 : dd > 60 ? 2 : dd > 30 ? 1 : 0] += Number(r.net_balance) || 0;
      return acc;
    }, [0, 0, 0]);
    if (view === "customer") {
      const header = `Customer,Customer Address,LR No.,LR Date,From Place,Destination,Bill No.,Bill Date,Bill Amount${tdsCols},Debit Note,Bill Submitted,On Account,Net Balance,No of Days,0-30 Days,31-60 Days,60+ Days`;
      csv = [
        header,
        ...rows.map((r) =>
          [r.customer_name, addrFor(r), r.lr_no, r.lr_date, r.from_place, r.destination, r.invoice_no, r.bill_date, r.total, ...tdsVals(r), r.debit_note || "", r.bill_submitted, r.on_account, r.net_balance, noDays(r), ...bucketVals(r)]
            .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
            .join(",")
        ),
        `"TOTAL OUTSTANDING",,,,,,,,${rows.reduce((s, r) => s + (Number(r.total) || 0), 0)},,,${rows.reduce((s, r) => s + (Number(r.on_account) || 0), 0)},${rows.reduce((s, r) => s + (Number(r.net_balance) || 0), 0)},,${bucketSums[0]},${bucketSums[1]},${bucketSums[2]}`,
      ].join("\n");
    } else {
      const header = `Branch,Customer Name,Customer Code,Customer Address,LR No.,LR Date,From,Destination,Bill No.,Bill Date,Bill Amount${tdsCols},Debit Note,Bill Submitted,On Account,Net Balance,No of Days,0-30 Days,31-60 Days,60+ Days`;
      csv = [
        header,
        ...rows.map((r) =>
          [r.office || "Chennai", r.customer_name, codeFor(r), addrFor(r), r.lr_no, r.lr_date, r.from_place, r.destination, r.invoice_no, r.bill_date, r.total, ...tdsVals(r), r.debit_note || "", r.bill_submitted, r.on_account, r.net_balance, noDays(r), ...bucketVals(r)]
            .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
            .join(",")
        ),
      ].join("\n");
    }
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `outstanding-${view}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Outstanding report exported");
  };

  const searchTerm = custSearch.trim().toLowerCase();
  const codeTerm = codeSearch.trim().toLowerCase();
  const visibleRows = data
    ? data.rows.filter((r) => {
        if (!(officeFilter === "All" || (r.office || "") === officeFilter)) return false;
        if (!(partyFilter === "All" || (r.customer_name || "") === partyFilter)) return false;
        if (codeTerm && !String(codeFor(r)).toLowerCase().includes(codeTerm)) return false;
        if (searchTerm && ![r.customer_name, r.invoice_no, r.lr_no].filter(Boolean).some((v) => String(v).toLowerCase().includes(searchTerm))) return false;
        const d = (r.bill_date || r.lr_date || "").slice(0, 10);
        if (fromDate && (!d || d < fromDate)) return false;
        if (toDate && (!d || d > toDate)) return false;
        return true;
      })
    : [];
  const filteredData = data ? { ...data, rows: visibleRows } : null;
  const onAcctByCust = data?.on_account_by_customer || {};
  const custCredit = (name) => Number(onAcctByCust[name] || 0);
  const visibleTotal = visibleRows.reduce((s, r) => s + (Number(r.net_balance) || 0), 0);
  const visibleCustomerNames = Array.from(new Set(visibleRows.map((r) => r.customer_name || "—")));
  const totalCredits = visibleCustomerNames.reduce((s, name) => s + custCredit(name), 0);
  const visibleNet = visibleTotal - totalCredits;
  const singleParty = partyFilter !== "All";
  const partyOptions = data
    ? Array.from(new Set(data.rows.map((r) => r.customer_name).filter(Boolean))).sort((a, b) => a.localeCompare(b))
    : [];
  const allCustomerNames = Array.from(new Set([...customers.map((c) => c.name), ...partyOptions].filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const displayOffice =
    officeFilter !== "All"
      ? officeFilter
      : (() => {
          const offs = Array.from(new Set(visibleRows.map((r) => r.office).filter(Boolean)));
          return offs.length === 1 ? offs[0] : null;
        })();
  const custShort = {};
  customers.forEach((c) => {
    const part = String(c.address || "").split(",").map((s) => s.trim()).filter(Boolean).pop() || c.state || "";
    const pin = String(c.pincode || "").trim();
    const short = String(c.billing_customer_address || "").trim() || (pin && part && !part.includes(pin) ? `${part}-${pin}` : part || pin);
    if (short) custShort[String(c.name || "").trim().toLowerCase()] = short;
  });
  const printOpts = { party: singleParty ? partyFilter : null, office: displayOffice, showTds: false, custShort };
  const selectedRows = visibleRows.filter(isSelected);
  const allVisibleSelected = visibleRows.length > 0 && visibleRows.every(isSelected);
  const toggleAll = () =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (allVisibleSelected) visibleRows.forEach((r) => n.delete(rowKey(r)));
      else visibleRows.forEach((r) => n.add(rowKey(r)));
      return n;
    });
  const printSelected = () => {
    if (selectedRows.length === 0) {
      toast.info("Select at least one bill to print");
      return;
    }
    printOutstanding({ ...data, rows: selectedRows }, view, printOpts);
  };

  const remindWhatsApp = (customer, custRows) => {
    const phone = custRows.find((r) => r.customer_phone)?.customer_phone;
    if (!phone) {
      toast.error(`No phone number saved for ${customer}. Add it in Customer Master.`);
      return;
    }
    const code = custRows.map((r) => codeFor(r)).find(Boolean) || "";
    const total = custRows.reduce((s, r) => s + (Number(r.net_balance) || 0), 0);
    const lines = custRows
      .map((r) => `• ${r.invoice_no || "Bill"} — ${inr(r.net_balance)}${r.days ? ` (${r.days}d overdue)` : ""}`)
      .join("\n");
    const msg = `Dear ${customer}${code ? ` (Customer Code: ${code})` : ""},\n\nThis is a gentle payment reminder from Nisha Transport Corporation.\n\nOutstanding bill(s):\n${lines}\n\nTotal Outstanding: ${inr(total)}\n\nKindly arrange the payment at your earliest convenience. If already paid, please ignore this message.\n\nThank you.`;
    window.open(waLink(phone, msg), "_blank");
    logReminder(customer, custRows.map((r) => stripInv(r.invoice_no)).filter(Boolean), total);
  };

  const [emailing, setEmailing] = useState("");
  const emailReminder = async (r) => {
    setEmailing(rowKey(r));
    try {
      const res = await api.post("/reminders/email", {
        customer_name: r.customer_name,
        bills: [{
          bill_no: stripInv(r.invoice_no),
          bill_date: r.bill_date ? fmtDate(r.bill_date) : r.issue_date ? fmtDate(r.issue_date) : "",
          from_to: `${r.from_place || "—"} → ${r.destination || "—"}`,
          amount: Number(r.net_balance) || 0,
          days: typeof noDays(r) === "number" ? noDays(r) : 0,
        }],
        total: Number(r.net_balance) || 0,
      });
      toast.success(`Reminder emailed to ${res.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email failed");
    } finally {
      setEmailing("");
    }
  };
  

  const shareStatementWhatsApp = (customer, custRows) => {
    const phone = custRows.find((r) => r.customer_phone)?.customer_phone;
    if (!phone) {
      toast.error(`No phone number saved for ${customer}. Add it in Customer Master.`);
      return;
    }
    const credit = custCredit(customer);
    const code = custRows.map((r) => codeFor(r)).find(Boolean) || "";
    const total = custRows.reduce((s, r) => s + (Number(r.net_balance) || 0), 0) - credit;
    const lines = custRows
      .map((r) => `• Bill ${stripInv(r.invoice_no)}${r.bill_date ? ` dt ${fmtDate(r.bill_date)}` : ""} — Amt ${inr(r.total)}, Balance ${inr(r.net_balance)}`)
      .join("\n");
    const msg = `*NISHA TRANSPORT CORPORATION*\nStatement of Outstanding — ${customer}${code ? ` (Customer Code: ${code})` : ""}\nAs on ${fmtDate(new Date().toISOString().slice(0, 10))}\n\n${lines}${credit > 0 ? `\n\nLess: On Account Credit — ${inr(credit)}` : ""}\n\n*NET OUTSTANDING: ${inr(total)}*\n\nKindly arrange payment at your earliest convenience. If already paid, please ignore this message.\n\nThank you for your business.`;
    window.open(waLink(phone, msg), "_blank");
    logReminder(customer, custRows.map((r) => stripInv(r.invoice_no)).filter(Boolean), total);
  };

  const overdueRows = visibleRows.filter((r) => {
    const d = noDays(r);
    return typeof d === "number" && d >= (Number(overdueDays) || 0);
  });
  const selectOverdue = () => {
    if (!overdueRows.length) {
      toast.info(`No bills overdue by ${overdueDays}+ days`);
      return;
    }
    setSelected(new Set(overdueRows.map(rowKey)));
    toast.success(`${overdueRows.length} bill(s) overdue by ${overdueDays}+ days selected`);
  };
  const remindOverdue = () => {
    if (!overdueRows.length) {
      toast.info(`No bills overdue by ${overdueDays}+ days`);
      return;
    }
    printBulkReminders(overdueRows, customers, profile, stampDays);
  };

  const customerGroups = data
    ? Object.values(
        visibleRows.reduce((acc, r) => {
          const key = r.customer_name || "—";
          (acc[key] = acc[key] || { customer: key, rows: [] }).rows.push(r);
          return acc;
        }, {})
      ).sort((a, b) => a.customer.localeCompare(b.customer))
    : [];

  const groupBalance = (g) => g.rows.reduce((s, r) => s + (Number(r.net_balance) || 0), 0) - custCredit(g.customer);
  const needsReremind = (g) => {
    if (!(groupBalance(g) > 0.01)) return false;
    const d = daysSinceReminded(g.customer);
    return d === null || d >= 7;
  };
  const reRemindGroups = customerGroups.filter(needsReremind);
  const remindAllNext = () => {
    const g = reRemindGroups.find((x) => x.rows.some((r) => r.customer_phone));
    if (!g) {
      toast.error("No phone numbers saved for the remaining customers — add them in Customer Master.");
      return;
    }
    remindWhatsApp(g.customer, g.rows);
    const left = reRemindGroups.length - 1;
    if (left > 0) toast.success(`Reminder opened for ${g.customer} — ${left} more. Tap "Remind All" again for the next.`);
    else toast.success(`Reminder opened for ${g.customer} — all re-remind-due customers covered.`);
  };

  const stateGroups = data
    ? Object.values(
        visibleRows.reduce((acc, r) => {
          const key = r.customer_state || "Not specified";
          const g = (acc[key] = acc[key] || { state: key, count: 0, amount: 0, balance: 0 });
          g.count += 1;
          g.amount += Number(r.total) || 0;
          g.balance += Number(r.net_balance) || 0;
          return acc;
        }, {})
      ).sort((a, b) => b.balance - a.balance)
    : [];

  return (
    <div>
      <PageHeader
        title="Outstanding Report"
        subtitle="Receivables — what customers owe you. Switch between bill-wise and customer-wise views."
      />

      <div className="mb-4 rounded-lg border border-primary/20 bg-primary/5 p-5">
        <div className="text-[11px] uppercase tracking-[0.18em] text-primary/80">
          Total Outstanding{officeFilter !== "All" ? ` — ${officeFilter}` : ""}
        </div>
        <div className="mt-1 font-display text-3xl font-bold text-primary" data-testid="total-outstanding">
          {data ? inr(visibleNet) : <Skeleton className="h-9 w-40" />}
        </div>
        {data && totalCredits > 0 && (
          <div className="mt-1 text-xs text-emerald-700" data-testid="total-on-account-credit">
            Includes On Account credit of {inr(totalCredits)} deducted from balance
          </div>
        )}
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <Button
          variant={view === "bill" ? "default" : "outline"}
          size="sm"
          className="gap-1.5"
          onClick={() => setView("bill")}
          data-testid="view-bill-wise"
        >
          <FileText className="h-4 w-4" /> Bill-wise
        </Button>
        <Button
          variant={view === "customer" ? "default" : "outline"}
          size="sm"
          className="gap-1.5"
          onClick={() => setView("customer")}
          data-testid="view-customer-wise"
        >
          <Users className="h-4 w-4" /> Customer-wise Outstanding
        </Button>
        <Popover open={partyOpen} onOpenChange={setPartyOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" size="sm" className="h-9 w-[220px] justify-between font-normal" data-testid="party-filter">
              <span className="flex min-w-0 items-center gap-1.5">
                <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{partyFilter === "All" ? "All Customers" : partyFilter}</span>
              </span>
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Type customer name…" data-testid="party-filter-search" />
              <CommandList className="max-h-[300px]">
                <CommandEmpty>No customer found.</CommandEmpty>
                <CommandItem value="all customers" onSelect={() => { setPartyFilter("All"); setPartyOpen(false); }} data-testid="party-option-all">
                  <Check className={`mr-2 h-4 w-4 ${partyFilter === "All" ? "opacity-100" : "opacity-0"}`} />
                  All Customers
                </CommandItem>
                {allCustomerNames.map((p) => (
                  <CommandItem key={p} value={p} onSelect={() => { setPartyFilter(p); setPartyOpen(false); }} data-testid="party-option">
                    <Check className={`mr-2 h-4 w-4 ${partyFilter === p ? "opacity-100" : "opacity-0"}`} />
                    {p}
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <div className="relative">
          <Input
            className="w-[150px]"
            placeholder="Customer Code"
            value={codeSearch}
            onChange={(e) => setCodeSearch(e.target.value)}
            data-testid="outstanding-customer-code-search"
          />
          {codeSearch && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 hover:text-slate-600"
              onClick={() => setCodeSearch("")}
              data-testid="outstanding-customer-code-clear"
            >
              ✕
            </button>
          )}
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="w-[230px] pl-8"
            placeholder="Search customer name…"
            value={custSearch}
            onChange={(e) => setCustSearch(e.target.value)}
            data-testid="outstanding-customer-search"
          />
          {custSearch && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 hover:text-slate-600"
              onClick={() => setCustSearch("")}
              data-testid="outstanding-customer-search-clear"
            >
              ✕
            </button>
          )}
        </div>
        <Button
          variant={view === "state" ? "default" : "outline"}
          size="sm"
          className="gap-1.5"
          onClick={() => setView("state")}
          data-testid="view-state-wise"
        >
          <MapPin className="h-4 w-4" /> State-wise
        </Button>
        {isAdmin && (
          <Select value={officeFilter} onValueChange={setOfficeFilter}>
            <SelectTrigger className="w-[170px]" data-testid="office-filter">
              <Building2 className="mr-1 h-4 w-4 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Branches</SelectItem>
              {OFFICES.map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {payScore && partyFilter !== "All" && (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold text-white ${payScore.score === "A" ? "bg-emerald-600" : payScore.score === "B" ? "bg-amber-600" : "bg-red-600"}`}
            title={`Payment Score ${payScore.score} — ${payScore.reason}`}
            data-testid="outstanding-payment-score-badge"
          >
            Score {payScore.score}
          </span>
        )}
        <div className="flex items-center gap-1.5">
          <Input type="date" className="w-[150px]" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="outstanding-from-date" title="Bill date from" />
          <span className="text-xs text-muted-foreground">to</span>
          <Input type="date" className="w-[150px]" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="outstanding-to-date" title="Bill date to" />
          {(fromDate || toDate) && (
            <Button variant="ghost" size="sm" onClick={() => { setFromDate(""); setToDate(""); }} data-testid="outstanding-date-clear">Clear</Button>
          )}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 rounded-md border border-border bg-slate-50 px-2 py-1" data-testid="overdue-control">
            <span className="text-xs font-medium text-slate-500">Overdue ≥</span>
            <Input
              type="number"
              value={overdueDays}
              onChange={(e) => setOverdueDays(Number(e.target.value))}
              className="h-7 w-16 text-xs"
              data-testid="overdue-days-input"
            />
            <span className="text-xs font-medium text-slate-500">days</span>
            <Button variant="secondary" size="sm" className="ml-1 h-7 gap-1 text-xs" onClick={selectOverdue} disabled={!data} data-testid="select-overdue-btn">
              Select Overdue{overdueRows.length ? ` (${overdueRows.length})` : ""}
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={remindOverdue}
            disabled={!data}
            data-testid="bulk-reminders-btn"
          >
            <BellRing className="h-4 w-4" /> Remind Overdue
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => exportCsv()} disabled={!data} data-testid="export-outstanding-btn">
            <Download className="h-4 w-4" /> Excel
          </Button>
          <Button variant="secondary" size="sm" className="gap-1.5" onClick={printSelected} disabled={!data || selectedRows.length === 0} data-testid="print-selected-btn">
            <Printer className="h-4 w-4" /> Print Selected{selectedRows.length ? ` (${selectedRows.length})` : ""}
          </Button>
          {isAdmin && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => printTdsDeductionSummary(visibleRows)} disabled={!data} data-testid="tds-summary-print-btn">
              <Printer className="h-4 w-4" /> TDS Summary
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={async () => {
                try {
                  const res = await api.get("/reports/note-register");
                  if (!res.data.credit_notes.length && !res.data.debit_notes.length) {
                    toast.info("No credit or debit notes issued yet.");
                    return;
                  }
                  printNoteRegister(res.data);
                } catch {
                  toast.error("Could not load note register");
                }
              }}
              data-testid="note-register-btn"
            >
              <Printer className="h-4 w-4" /> Note Register
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={async () => {
                try {
                  const res = await api.get("/reports/note-register");
                  if (!res.data.credit_notes.length && !res.data.debit_notes.length) {
                    toast.info("No credit or debit notes issued yet.");
                    return;
                  }
                  printNoteSummary(res.data);
                } catch {
                  toast.error("Could not load note summary");
                }
              }}
              data-testid="note-summary-btn"
            >
              <Printer className="h-4 w-4" /> Note Summary (FY)
            </Button>
          )}
          {isAdmin && <NoteEmailDialog />}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={async () => {
                try {
                  const res = await api.get("/notes/decisions");
                  if (!res.data.decisions.length) {
                    toast.info("No note approvals or rejections recorded yet.");
                    return;
                  }
                  printNoteApprovalLog(res.data.decisions);
                } catch {
                  toast.error("Could not load approval log");
                }
              }}
              data-testid="approval-log-btn"
            >
              <History className="h-4 w-4" /> Approval Log
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 border-slate-400 font-semibold"
              onClick={async () => {
                try {
                  const [reg, dec] = await Promise.all([api.get("/reports/note-register"), api.get("/notes/decisions")]);
                  if (!reg.data.credit_notes.length && !reg.data.debit_notes.length && !dec.data.decisions.length) {
                    toast.info("No notes or approval actions to bundle yet.");
                    return;
                  }
                  printNoteAuditPack(reg.data, dec.data.decisions);
                } catch {
                  toast.error("Could not build the audit pack");
                }
              }}
              data-testid="audit-pack-btn"
            >
              <Printer className="h-4 w-4" /> Audit Pack
            </Button>
          )}
          {isAdmin && <PendingNotesDialog onDecided={() => api.get("/reports/outstanding").then((r) => setData(r.data)).catch(() => {})} />}
          <ReminderHistoryDialog partyFilter={partyFilter} customers={customers} />
          <DisputedBillsDialog />
          <AutoWeeklyDialog onStampChange={setStampDays} />
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => printOutstanding(filteredData, view, printOpts)} disabled={!data} data-testid="print-outstanding-btn">
            <Printer className="h-4 w-4" /> Print
          </Button>
        </div>
      </div>

      {data && partyFilter !== "All" && visibleRows.length === 0 && (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-emerald-300 bg-emerald-50/70 px-4 py-3" data-testid="zero-due-indicator">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-emerald-100 text-emerald-600">
            <Check className="h-5 w-5" />
          </span>
          <div>
            <div className="text-sm font-bold text-emerald-800">No outstanding — {partyFilter}</div>
            <div className="text-xs text-emerald-700">This customer has no pending dues. All bills are fully settled. 🎉</div>
          </div>
        </div>
      )}

      {data && reRemindGroups.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50/70 px-4 py-3" data-testid="reremind-alert">
          <div className="flex items-center gap-2">
            <BellRing className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-bold text-amber-800" data-testid="reremind-count">
              Re-Remind Due ({reRemindGroups.length}) — customers with dues not reminded in the last 7 days
            </span>
            <button
              type="button"
              className="ml-auto flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-1 text-xs font-bold text-white hover:bg-emerald-700"
              title="Opens WhatsApp for the next due customer — tap once per customer"
              onClick={remindAllNext}
              data-testid="reremind-all-btn"
            >
              <MessageCircle className="h-3.5 w-3.5" /> Remind All ({reRemindGroups.length})
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {reRemindGroups.map((g) => {
              const d = daysSinceReminded(g.customer);
              return (
                <span key={g.customer} className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-800" data-testid="reremind-chip">
                  <button
                    type="button"
                    className="hover:underline"
                    title="View this customer's outstanding"
                    onClick={() => { setPartyFilter(g.customer); setView("customer"); }}
                    data-testid="reremind-chip-name"
                  >
                    {g.customer}
                  </button>
                  <b>{inr(groupBalance(g))}</b>
                  <span className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{d === null ? "Never reminded" : `${d}d ago`}</span>
                  <button
                    type="button"
                    className="flex items-center gap-0.5 rounded-full bg-emerald-600 px-1.5 py-0.5 text-[10px] font-bold text-white hover:bg-emerald-700"
                    title="Send WhatsApp reminder now"
                    onClick={() => remindWhatsApp(g.customer, g.rows)}
                    data-testid="reremind-wa-btn"
                  >
                    <MessageCircle className="h-3 w-3" /> WA
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {view === "state" ? (
        <TableShell>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {["State", "No. of Bills", "Bill Amount", "Outstanding"].map((h) => (
                  <TableHead
                    key={h}
                    className={`text-[11px] uppercase tracking-[0.15em] text-slate-500 ${
                      h !== "State" ? "text-right" : ""
                    }`}
                  >
                    {h}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data === null &&
                [1, 2, 3].map((i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 4 }).map((_, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-24" /></TableCell>
                    ))}
                  </TableRow>
                ))}
              {data && stateGroups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="py-12 text-center text-muted-foreground">No outstanding. 🎉</TableCell>
                </TableRow>
              )}
              {data &&
                stateGroups.map((g) => (
                  <TableRow key={g.state} data-testid="state-row">
                    <TableCell className="text-sm font-medium">{g.state}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{g.count}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{inr(g.amount)}</TableCell>
                    <TableCell className="text-right text-sm font-semibold tabular-nums text-primary">{inr(g.balance)}</TableCell>
                  </TableRow>
                ))}
              {data && stateGroups.length > 0 && (
                <TableRow className="bg-slate-100 font-bold" data-testid="state-total-row">
                  <TableCell className="text-right text-sm">TOTAL</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{stateGroups.reduce((s, g) => s + g.count, 0)}</TableCell>
                  <TableCell></TableCell>
                  <TableCell className="text-right text-sm tabular-nums text-primary">{inr(stateGroups.reduce((s, g) => s + g.balance, 0))}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableShell>
      ) : view === "customer" ? (
        <TableShell>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="w-10">
                  <Checkbox checked={allVisibleSelected} onCheckedChange={toggleAll} data-testid="select-all-bills-cust" aria-label="Select all bills" />
                </TableHead>
                {["LR No.", "LR Date", "From Place", "Destination", "Bill No.", "Bill Date", "Bill Amount", ...(isAdmin ? ADMIN_COLS : []), "Bill Submitted", "On Account", "Net Balance", "No of Days"].map(
                  (h) => (
                    <TableHead
                      key={h}
                      className={`whitespace-nowrap text-[11px] uppercase tracking-[0.15em] text-slate-500 ${
                        ["Bill Amount", ...ADMIN_COLS, "On Account", "Net Balance"].includes(h) ? "text-right" : ""
                      }`}
                    >
                      {h}
                    </TableHead>
                  )
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data === null &&
                [1, 2, 3].map((i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 9 }).map((_, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-20" /></TableCell>
                    ))}
                  </TableRow>
                ))}
              {data && visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12 + extra} className="py-12 text-center text-muted-foreground">
                    No outstanding by any customer. 🎉
                  </TableCell>
                </TableRow>
              )}
              {data &&
                customerGroups.map((g) => (
                  <Fragment key={g.customer}>
                    <TableRow className="bg-slate-100 hover:bg-slate-100">
                      <TableCell colSpan={12 + extra} className="text-sm font-bold text-slate-700" data-testid="customer-group-header">
                        <div className="flex items-center justify-between gap-2">
                          <span className="flex items-center gap-2">
                            <span>{g.rows.find((r) => r.customer_code)?.customer_code ? `${g.rows.find((r) => r.customer_code).customer_code} — ${g.customer}` : g.customer}</span>
                            {needsReremind(g) ? (
                              <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white" title="Outstanding dues — no reminder sent in the last 7 days" data-testid="group-reremind-badge">
                                {daysSinceReminded(g.customer) === null ? "Never reminded" : `Not reminded ${daysSinceReminded(g.customer)}d`}
                              </span>
                            ) : daysSinceReminded(g.customer) !== null ? (
                              <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-600" data-testid="group-reminded-badge">
                                Reminded {daysSinceReminded(g.customer) === 0 ? "today" : `${daysSinceReminded(g.customer)}d ago`}
                              </span>
                            ) : null}
                          </span>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 gap-1.5 text-primary hover:text-primary"
                              title="Generate this customer's statement PDF"
                              onClick={() => downloadStatement(g.customer, g.rows)}
                              data-testid="statement-group-btn"
                            >
                              <FileText className="h-4 w-4" /> Statement
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 gap-1.5 text-emerald-600 hover:text-emerald-700"
                              title="Send full outstanding statement on WhatsApp"
                              onClick={() => shareStatementWhatsApp(g.customer, g.rows)}
                              data-testid="statement-whatsapp-btn"
                            >
                              <MessageCircle className="h-4 w-4" /> Statement → WA
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 gap-1.5 text-emerald-600 hover:text-emerald-700"
                              title="Send WhatsApp reminder for all these bills"
                              onClick={() => remindWhatsApp(g.customer, g.rows)}
                              data-testid="whatsapp-group-btn"
                            >
                              <MessageCircle className="h-4 w-4" /> WhatsApp
                            </Button>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                    {g.rows.map((r) => (
                      <TableRow key={rowKey(r)} className="whitespace-nowrap" data-testid="customer-detail-row">
                        <TableCell className="w-10">
                          <Checkbox checked={isSelected(r)} onCheckedChange={() => toggleRow(r)} data-testid="select-bill-cust" aria-label="Select bill" />
                        </TableCell>
                        <TableCell className="text-sm font-medium">{r.lr_no || "—"}</TableCell>
                        <TableCell className="text-sm">{fmtDate(r.lr_date)}</TableCell>
                        <TableCell className="text-sm">{r.from_place || "—"}</TableCell>
                        <TableCell className="text-sm">{r.destination || "—"}</TableCell>
                        <TableCell className="text-sm">
                          {stripInv(r.invoice_no)}
                          {r.status === "Partly Paid" && (
                            <span className="ml-1.5 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700">Partly Paid</span>
                          )}
                          {Number(r.deduction_pending) > 0 && (
                            <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" data-testid="deduction-pending-badge-cust">
                              Deduction {inr(r.deduction_pending)} — pending approval
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{fmtDate(r.bill_date)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{inr(r.total)}</TableCell>
                        {isAdmin &&
                          ADMIN_KEYS.map((k) => (
                            <TableCell key={k} className="text-right text-sm tabular-nums text-amber-700" data-testid={`${k}-cell-cust`}>
                              {Number(r[k]) ? inr(r[k]) : "—"}
                            </TableCell>
                          ))}
                        <TableCell className="text-sm">
                          {r.bill_submitted ? `${r.bill_submitted}${r.bill_submitted_date ? ` (${fmtDate(r.bill_submitted_date)})` : ""}` : "—"}
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{inr(r.on_account)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{inr(r.net_balance)}</TableCell>
                        <TableCell className="text-sm">{noDays(r)}</TableCell>
                      </TableRow>
                    ))}
                    {custCredit(g.customer) > 0 && (
                      <TableRow className="bg-emerald-50/60" data-testid="customer-onaccount-credit-row">
                        <TableCell colSpan={10 + extra} className="text-right text-xs font-semibold uppercase tracking-wide text-emerald-700">
                          Less: On Account (Advance / Part Payment)
                        </TableCell>
                        <TableCell className="text-right text-sm font-semibold tabular-nums text-emerald-700">
                          - {inr(custCredit(g.customer))}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    )}
                    <TableRow className="bg-slate-50/70">
                      <TableCell colSpan={7} className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Subtotal — {g.customer}
                      </TableCell>
                      <TableCell className="text-right text-sm font-semibold tabular-nums">
                        {inr(g.rows.reduce((s, r) => s + (Number(r.total) || 0), 0))}
                      </TableCell>
                      {isAdmin &&
                        ADMIN_KEYS.map((k) => (
                          <TableCell key={k} className="text-right text-sm font-semibold tabular-nums text-amber-700" data-testid={`${k}-subtotal`}>
                            {inr(adminSum(g.rows, k))}
                          </TableCell>
                        ))}
                      <TableCell></TableCell>
                      <TableCell className="text-right text-sm font-semibold tabular-nums">
                        {inr(g.rows.reduce((s, r) => s + (Number(r.on_account) || 0), 0))}
                      </TableCell>
                      <TableCell className="text-right text-sm font-semibold tabular-nums">
                        {inr(g.rows.reduce((s, r) => s + (Number(r.net_balance) || 0), 0) - custCredit(g.customer))}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </Fragment>
                ))}
              {data && visibleRows.length > 0 && (
                <TableRow className="bg-slate-100 font-bold" data-testid="customer-total-row">
                  <TableCell colSpan={7} className="text-right text-sm">TOTAL OUTSTANDING</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {inr(visibleRows.reduce((s, r) => s + (Number(r.total) || 0), 0))}
                  </TableCell>
                  {isAdmin &&
                    ADMIN_KEYS.map((k) => (
                      <TableCell key={k} className="text-right text-sm tabular-nums text-amber-700">
                        {inr(adminSum(visibleRows, k))}
                      </TableCell>
                    ))}
                  <TableCell></TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {inr(visibleRows.reduce((s, r) => s + (Number(r.on_account) || 0), 0))}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums text-primary">
                    {inr(visibleNet)}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableShell>
      ) : (
      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-10">
                <Checkbox checked={allVisibleSelected} onCheckedChange={toggleAll} data-testid="select-all-bills" aria-label="Select all bills" />
              </TableHead>
              {["Branch", "Customer Name", "Customer Code", "LR No.", "LR Date", "From", "Destination", "Bill No.", "Bill Date", "Bill Amount", ...(isAdmin ? ADMIN_COLS : []), "Bill Submitted", "On Account", "Net Balance", "No of Days"]
                .filter((h) => !(singleParty && (h === "Customer Name" || h === "Customer Code")))
                .map(
                (h) => (
                  <TableHead
                    key={h}
                    className={`whitespace-nowrap text-[11px] uppercase tracking-[0.15em] text-slate-500 ${
                      ["Bill Amount", ...ADMIN_COLS, "On Account", "Net Balance"].includes(h) ? "text-right" : ""
                    }`}
                  >
                    {h}
                  </TableHead>
                )
              )}
              <TableHead className="whitespace-nowrap text-right text-[11px] uppercase tracking-[0.15em] text-slate-500">
                Reminder
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data === null &&
              [1, 2, 3].map((i) => (
                <TableRow key={i}>
                  {Array.from({ length: 12 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {data && visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={(singleParty ? 14 : 15) + extra} className="py-12 text-center text-muted-foreground">
                  No outstanding invoices — all receivables are collected. 🎉
                </TableCell>
              </TableRow>
            )}
            {data &&
              visibleRows.map((r) => (
                <TableRow key={rowKey(r)} data-testid="outstanding-row" className="whitespace-nowrap">
                  <TableCell className="w-10">
                    <Checkbox checked={isSelected(r)} onCheckedChange={() => toggleRow(r)} data-testid="select-bill" aria-label="Select bill" />
                  </TableCell>
                  <TableCell className="text-sm font-medium">{r.office || "Chennai"}</TableCell>
                  {!singleParty && <TableCell className="text-sm font-medium">{r.customer_name || "—"}</TableCell>}
                  {!singleParty && <TableCell className="text-sm" data-testid="outstanding-code-cell">{codeFor(r) || "—"}</TableCell>}
                  <TableCell className="text-sm font-medium">{r.lr_no || "—"}</TableCell>
                  <TableCell className="text-sm">{r.lr_date ? fmtDate(r.lr_date) : "—"}</TableCell>
                  <TableCell className="text-sm">{r.from_place || "—"}</TableCell>
                  <TableCell className="text-sm">{r.destination || "—"}</TableCell>
                  <TableCell className="text-sm font-medium">
                    {stripInv(r.invoice_no)}
                    {r.status === "Partly Paid" && (
                      <span className="ml-1.5 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700" data-testid="partly-paid-badge">Partly Paid</span>
                    )}
                    {Number(r.deduction_pending) > 0 && (
                      <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" data-testid="deduction-pending-badge">
                        Deduction {inr(r.deduction_pending)} — pending approval
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{r.bill_date ? fmtDate(r.bill_date) : "—"}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{inr(r.total)}</TableCell>
                  {isAdmin &&
                    ADMIN_KEYS.map((k) => (
                      <TableCell key={k} className="text-right text-sm tabular-nums text-amber-700" data-testid={`${k}-cell`}>
                        {Number(r[k]) ? inr(r[k]) : "—"}
                      </TableCell>
                    ))}
                  <TableCell className="text-sm">
                    <span
                      className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                        r.bill_submitted === "Not Submitted"
                          ? "border-slate-200 text-slate-500"
                          : "border-emerald-200 bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      {r.bill_submitted}
                      {r.bill_submitted_date ? ` (${fmtDate(r.bill_submitted_date)})` : ""}
                    </span>
                  </TableCell>
                  <TableCell className="text-right text-sm font-semibold tabular-nums">{inr(r.on_account)}</TableCell>
                  <TableCell className="text-right text-sm font-semibold tabular-nums">{inr(r.net_balance)}</TableCell>
                  <TableCell className="text-sm">{noDays(r)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-emerald-600 hover:text-emerald-700"
                        title="Send WhatsApp reminder"
                        onClick={() => remindWhatsApp(r.customer_name, [r])}
                        data-testid="whatsapp-reminder-btn"
                      >
                        <MessageCircle className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-sky-600 hover:text-sky-700"
                        title="Email payment reminder letter"
                        onClick={() => emailReminder(r)}
                        disabled={emailing === rowKey(r)}
                        data-testid="email-reminder-btn"
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-primary"
                        title="Print payment reminder letter"
                        onClick={() => printPaymentReminder(r, customers.find((x) => (r.customer_id && x.id === r.customer_id) || (x.name || "").trim().toLowerCase() === String(r.customer_name || "").trim().toLowerCase()), profile, stampDays)}
                        data-testid="print-reminder-btn"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            {data && visibleRows.length > 0 && (
              <TableRow className="bg-slate-50 font-semibold" data-testid="outstanding-total-row">
                <TableCell colSpan={singleParty ? 8 : 9} className="text-right text-sm">TOTAL OUTSTANDING</TableCell>
                <TableCell className="text-right text-sm tabular-nums">
                  {inr(visibleRows.reduce((s, r) => s + (Number(r.total) || 0), 0))}
                </TableCell>
                {isAdmin &&
                  ADMIN_KEYS.map((k) => (
                    <TableCell key={k} className="text-right text-sm tabular-nums text-amber-700" data-testid={`${k}-total`}>
                      {inr(adminSum(visibleRows, k))}
                    </TableCell>
                  ))}
                <TableCell></TableCell>
                <TableCell className="text-right text-sm tabular-nums">
                  {inr(visibleRows.reduce((s, r) => s + (Number(r.on_account) || 0), 0))}
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums text-primary">
                  {inr(visibleRows.reduce((s, r) => s + (Number(r.net_balance) || 0), 0))}
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableShell>
      )}
    </div>
  );
}
